import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { type User, type AuthState, getPermissions, type Permission } from '@packages/types/auth';

interface AuthStore extends AuthState {
  login: (user: User) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  getPermissions: () => Permission;
  updateUser: (user: User) => void;
}

/**
 * persist rehydration이 로그인 직후 늦게 끝나면 localStorage의 stale isAuthenticated=false 가
 * 메모리 상태를 덮어써 모바일에서 "로그인이 안 되는 것처럼" 보일 수 있다.
 */
let sessionLoginActive = false;

/** JWT sessionStorage + expiry (로그인·FCM·ProtectedRoute 공통 기준) */
export function hasValidSessionToken(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  try {
    const token = sessionStorage.getItem('access_token');
    const tokenExpiry = sessionStorage.getItem('token_expiry');
    return !!token && (!tokenExpiry || new Date() <= new Date(tokenExpiry));
  } catch {
    return false;
  }
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,

      login: (user: User) => {
        sessionLoginActive = true;
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      logout: () => {
        sessionLoginActive = false;
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      getPermissions: () => {
        const { user } = get();
        const roleName = user?.role_name || 'Guest';
        const permissions = getPermissions(roleName);
        return permissions;
      },

      updateUser: (user: User) => {
        // ZUSTAND: 사용자 정보 업데이트
        set({ user });
      },
    }),
    {
      name: 'auth-storage',
      partialize: state => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      merge: (persisted, current) => {
        const base = {
          ...current,
          ...(persisted as Partial<AuthStore>),
        };
        if (sessionLoginActive || hasValidSessionToken()) {
          return { ...base, ...current, isAuthenticated: current.isAuthenticated || base.isAuthenticated };
        }
        if (!hasValidSessionToken()) {
          return { ...base, isAuthenticated: false, user: null };
        }
        return base;
      },
      onRehydrateStorage: () => state => {
        if (!state) return;
        if (sessionLoginActive) return;
        if (hasValidSessionToken()) {
          state.isAuthenticated = true;
          return;
        }
        state.isAuthenticated = false;
        state.user = null;
      },
    }
  )
);

// Development helper
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).__AUTH_STORE__ = useAuthStore;

  (window as any).getAuthState = () => {
    const state = useAuthStore.getState();

    return state;
  };

  (window as any).watchAuthStore = () => {
    console.log('👀 Watching auth store changes...');
    return useAuthStore.subscribe(state => {
      console.log('🔄 [Auth Store Update]', {
        isAuthenticated: state.isAuthenticated,
        user: state.user?.email,
        role: state.user?.role_name,
      });
    });
  };

  // console.log('🛠️ Auth Store Debug Tools Available:');
  // console.log('  - getAuthState() : 현재 인증 상태 확인');
  // console.log('  - watchAuthStore() : 실시간 변화 감지');
  // console.log('  - __AUTH_STORE__.getState() : 전체 상태');
}
